import { useAuth } from '@/contexts/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Home, Clock, Bell, User, Shield, LogOut, ChevronRight, Download, Send, Package, Plane, Search, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InstallAppButton } from '@/components/install/InstallAppButton';

const LOGO_URL = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_crypto-remit-1/artifacts/hfmyqknz_ChatGPT%20Image%20Sep%2016%2C%202026%2C%2002_56_22%20PM.png';

// Bottom nav configs per module
const NAV = {
  send: [
    { path: '/send/dashboard', icon: Home, label: 'Accueil' },
    { path: '/send/new', icon: Send, label: 'Envoyer' },
    { path: '/send/history', icon: Clock, label: 'Transferts' },
  ],
  colis: [
    { path: '/colis', icon: Search, label: 'Chercher' },
    { path: '/colis/my-bookings', icon: Package, label: 'Suivi' },
    { path: '/colis/publish', icon: Plane, label: 'Publier' },
  ],
  agent: [
    { path: '/agent', icon: Home, label: 'Accueil' },
    { path: '/notifications', icon: Bell, label: 'Alertes' },
    { path: '/profile', icon: User, label: 'Profil' },
  ],
  admin: [
    { path: '/admin', icon: Shield, label: 'Admin' },
    { path: '/profile', icon: User, label: 'Profil' },
  ],
};

// Module tabs — MboaSend users see Send+Colis, Transfer users see Transfer only
const MODULES_SEND = [
  { key: 'send', path: '/send/dashboard', label: 'MboaSend', color: '#006400' },
];

// Desktop sidebars
const SIDEBARS = {
  send: [
    { type: 'link', path: '/send/dashboard', icon: Home, label: 'Accueil' },
    { type: 'link', path: '/send/deposit', icon: Download, label: 'Ajouter de l\'argent' },
    { type: 'link', path: '/send/new', icon: Send, label: 'Envoyer au Cameroun' },
    { type: 'link', path: '/send/history', icon: Clock, label: 'Mes transferts' },
    { type: 'divider', label: 'Compte' },
    { type: 'link', path: '/kyc', icon: Shield, label: 'Vérification identité' },
    { type: 'link', path: '/notifications', icon: Bell, label: 'Notifications' },
    { type: 'link', path: '/profile', icon: User, label: 'Profil' },
  ],
  agent: [
    { type: 'link', path: '/agent', icon: Home, label: 'Dashboard Agent' },
    { type: 'link', path: '/notifications', icon: Bell, label: 'Notifications' },
    { type: 'link', path: '/profile', icon: User, label: 'Profil' },
  ],
  admin: [
    { type: 'link', path: '/admin', icon: Shield, label: 'Dashboard Admin' },
    { type: 'link', path: '/profile', icon: User, label: 'Profil' },
  ],
};

function detectModule(pathname) {
  if (pathname.startsWith('/colis')) return 'colis';
  if (pathname.startsWith('/send')) return 'send';
  return null;
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // MboaSend only (wallet, transfer, trading and investor spaces were removed): every client account uses the MboaSend layout
  const isMboaSend = true;

  const currentModule = detectModule(location.pathname);
  // MboaSend users: send + colis modules. Transfer users: transfer only (standalone).
  const activeModule = currentModule || 'send';
  const showModuleSwitcher = isMboaSend && user?.role === 'user' && MODULES_SEND.length > 1;

  // Bottom nav items
  const navItems = ['admin','super_admin'].includes(user?.role) ? NAV.admin :
    user?.role === 'agent' ? NAV.agent :
    activeModule && NAV[activeModule] ? NAV[activeModule] :
    NAV.send;

  // Desktop sidebar
  const sidebarKey = user?.role === 'agent' ? 'agent' : ['admin','super_admin'].includes(user?.role) ? 'admin' :
    'send';
  const sidebarItems = SIDEBARS[sidebarKey] || SIDEBARS.send;

  // Light theme applies to Send + Agent + Admin modules (phase 3 rebranding).
  // These modules support a user toggle (light default, dark optional) — persisted in localStorage.
  const canTheme = sidebarKey === 'send' || sidebarKey === 'agent' || sidebarKey === 'admin' || activeModule === 'send';
  const [themePref, setThemePref] = useState(() => {
    const stored = localStorage.getItem('mboa-theme');
    if (stored) return stored;
    // First load: follow the user's system preference until they explicitly choose.
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const light = canTheme ? themePref !== 'dark' : false;
  const forceDark = canTheme && !light;

  const toggleTheme = () => {
    const next = themePref === 'dark' ? 'light' : 'dark';
    setThemePref(next);
    localStorage.setItem('mboa-theme', next);
  };

  useEffect(() => {
    document.body.classList.toggle('send-light-theme', light);
    document.body.classList.toggle('force-dark', forceDark);
    return () => {
      document.body.classList.remove('send-light-theme');
      document.body.classList.remove('force-dark');
    };
  }, [light, forceDark]);

  const P = light ? {
    shell: 'bg-[#FAF9F5]',
    aside: 'bg-white border-r border-slate-200',
    borderTop: 'border-t border-slate-200',
    borderBottom: 'border-b border-slate-200',
    dividerText: 'text-slate-400',
    navActive: 'bg-[#006400]/10 text-[#006400]',
    navIdle: 'text-slate-600 hover:bg-slate-100 hover:text-[#006400]',
    brandA: 'text-[#006400]', brandB: 'text-[#B8860B]',
    userName: 'text-[#0D2612]', userRole: 'text-slate-500',
    avatarBg: 'bg-[#006400]/10', avatarText: 'text-[#006400]',
    logoutBtn: 'text-slate-600 hover:text-red-600 hover:bg-red-50',
    headerBar: 'border-b border-slate-200 bg-white/95 backdrop-blur-lg',
    iconMuted: 'text-slate-500',
    switcherBar: 'bg-white border-b border-slate-200',
    switcherIdle: 'text-slate-500',
    bottomNav: 'bg-white/95 backdrop-blur-lg border-t border-slate-200',
    navItemActive: 'text-[#006400]', navItemIdle: 'text-slate-500',
  } : {
    shell: 'bg-[#0A0A0A]',
    aside: 'bg-[#0A0A0A] border-r border-[#27272A]',
    borderTop: 'border-t border-[#27272A]',
    borderBottom: 'border-b border-[#27272A]',
    dividerText: 'text-[#52525B]',
    navActive: 'bg-[#FFD700]/10 text-[#FFD700]',
    navIdle: 'text-[#A1A1AA] hover:bg-[#18181B] hover:text-white',
    brandA: 'text-[#FFD700]', brandB: 'text-[#00C853]',
    userName: 'text-white', userRole: 'text-[#A1A1AA]',
    avatarBg: 'bg-[#FFD700]/20', avatarText: 'text-[#FFD700]',
    logoutBtn: 'text-[#A1A1AA] hover:text-red-400 hover:bg-red-400/10',
    headerBar: 'border-b border-[#27272A] bg-[#0A0A0A]/95 backdrop-blur-lg',
    iconMuted: 'text-[#A1A1AA]',
    switcherBar: 'bg-[#0A0A0A] border-b border-[#27272A]',
    switcherIdle: 'text-[#71717A]',
    bottomNav: 'bg-[#0A0A0A]/95 backdrop-blur-lg border-t border-[#27272A]',
    navItemActive: 'text-[#FFD700]', navItemIdle: 'text-[#A1A1AA]',
  };

  const isActive = (path) => {
    if (path === '/colis') return location.pathname === '/colis';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Brand name based on context
  const getBrandName = () => {
    if (activeModule === 'colis') return { a: 'Send', b: 'Colis' };
    if (isMboaSend) return { a: 'MBOA', b: 'Send' };
    return null;
  };
  const brand = getBrandName();

  return (
    <div className={`min-h-screen ${P.shell}`}>
      {/* Desktop Sidebar */}
      <aside className={`w-64 fixed h-full ${P.aside} hidden md:flex flex-col z-50`} data-testid="desktop-sidebar">
        <div className={`p-6 ${P.borderBottom} flex-shrink-0`}>
          <Link to={brand ? (activeModule === 'colis' ? '/sendcolis' : '/send') : '/'} className="flex items-center gap-3">
            <img src={LOGO_URL} alt="MBOA" className="w-10 h-10 rounded-lg" />
            {brand ? (
              <span style={{ fontFamily: 'Outfit' }}><span className={`font-semibold ${P.brandA} text-lg`}>{brand.a}</span><span className={`font-light ${P.brandB} text-lg`}>{brand.b}</span></span>
            ) : (
              <span className={`font-semibold ${P.brandA} text-lg`} style={{ fontFamily: 'Outfit' }}>MBOA</span>
            )}
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-0.5 overflow-y-auto">
          {sidebarItems.map((item, idx) => {
            if (item.type === 'divider') {
              return (
                <div key={idx} className="pt-4 pb-1.5 px-4">
                  <p className={`text-[10px] font-semibold ${P.dividerText} uppercase tracking-wider`}>{item.label}</p>
                </div>
              );
            }
            const active = isActive(item.path);
            return (
              <Link key={item.path} to={item.path}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/[\s']/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                  active ? P.navActive : P.navIdle
                }`}>
                <item.icon className="w-4 h-4" />
                <span className="text-sm">{item.label}</span>
                {active && <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        <div className={`p-4 ${P.borderTop} flex-shrink-0`}>
          {canTheme && (
            <Button variant="ghost" className={`w-full justify-start mb-1 ${P.navIdle}`}
              onClick={toggleTheme} data-testid="theme-toggle-btn">
              {light ? <Moon className="w-4 h-4 mr-2" /> : <Sun className="w-4 h-4 mr-2" />}
              {light ? 'Mode sombre' : 'Mode clair'}
            </Button>
          )}
          <InstallAppButton className={P.navIdle} />
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className={`w-8 h-8 rounded-full ${P.avatarBg} flex items-center justify-center flex-shrink-0`}>
              <span className={`${P.avatarText} text-sm font-bold`}>{user?.first_name?.[0]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm ${P.userName} truncate`}>{user?.first_name} {user?.last_name}</p>
              <p className={`text-xs ${P.userRole} capitalize`}>{user?.role}</p>
            </div>
          </div>
          <Button variant="ghost" className={`w-full justify-start ${P.logoutBtn}`}
            onClick={() => { logout(); navigate('/'); }} data-testid="sidebar-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="md:pl-64 min-h-screen pb-28 md:pb-0">
        {/* Mobile Header */}
        <div className="md:hidden sticky top-0 z-40">
          {/* Top header */}
          <div className={`flex items-center justify-between p-3 ${P.headerBar}`}>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MBOA" className="w-7 h-7 rounded-lg" />
              {brand ? (
                <span style={{ fontFamily: 'Outfit' }} className="text-sm"><span className={`font-semibold ${P.brandA}`}>{brand.a}</span><span className={`font-light ${P.brandB}`}>{brand.b}</span></span>
              ) : (
                <span className={`font-semibold ${P.brandA} text-sm`} style={{ fontFamily: 'Outfit' }}>MBOA</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {canTheme && (
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleTheme} data-testid="mobile-theme-toggle-btn">
                  {light ? <Moon className={`w-4 h-4 ${P.iconMuted}`} /> : <Sun className={`w-4 h-4 ${P.iconMuted}`} />}
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/notifications')} data-testid="mobile-notifications-btn">
                <Bell className={`w-4 h-4 ${P.iconMuted}`} />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/profile')} data-testid="mobile-profile-btn">
                <div className={`w-6 h-6 rounded-full ${P.avatarBg} flex items-center justify-center`}>
                  <span className={`${P.avatarText} text-[10px] font-bold`}>{user?.first_name?.[0]}</span>
                </div>
              </Button>
            </div>
          </div>

          {/* Module switcher tabs (MboaSend users: Send + Colis) */}
          {showModuleSwitcher && (
            <div className={`flex ${P.switcherBar} px-2 py-1 gap-1`} data-testid="module-switcher">
              {MODULES_SEND.map(m => {
                const isAct = activeModule === m.key;
                return (
                  <Link key={m.key} to={m.path} data-testid={`module-${m.key}`}
                    className={`flex-1 py-1.5 rounded-lg text-center text-[11px] font-medium transition-all ${
                      isAct ? 'text-white' : P.switcherIdle
                    }`}
                    style={isAct ? { backgroundColor: m.color, color: '#fff' } : {}}>
                    {m.label}
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className={`fixed bottom-0 left-0 right-0 h-14 ${P.bottomNav} flex justify-around items-center z-50 md:hidden`} data-testid="mobile-bottom-nav">
        {navItems.map(item => {
          const active = item.path === '/colis'
            ? location.pathname === '/colis'
            : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          return (
            <Link key={item.path} to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 transition-colors ${
                active ? P.navItemActive : P.navItemIdle
              }`}>
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Telegram support button (MboaSend users) */}
      {isMboaSend && (
        <a href="https://t.me/mboasendbot" target="_blank" rel="noopener noreferrer"
          className="fixed bottom-20 right-4 w-12 h-12 rounded-full bg-[#0088CC] flex items-center justify-center shadow-lg z-50 hover:scale-110 transition-transform"
          data-testid="telegram-support-btn">
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
        </a>
      )}
    </div>
  );
}
